import { useState } from 'react'
import emailjs from '@emailjs/browser'

export default function Contact() {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

  const fieldInvalid = (value: string, isEmail = false) =>
    submitted && (!value.trim() || (isEmail && !validateEmail(value)))

  const inputClass = (invalid: boolean) =>
    `mt-2 block w-full rounded-xl border px-4 py-3 text-base text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 bg-white transition-colors ${
      invalid
        ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20'
        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20'
    }`

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitted(true)
    if (!firstName.trim() || !company.trim() || !email.trim() || !validateEmail(email) || !description.trim()) return
    setError('')
    setSending(true)
    emailjs
      .send(
        'service_ww3g9ia',
        'template_lndule5',
        { email, firstName, lastName, company, phone, description },
        '-e1zvYkZsn6j9az8z',
      )
      .then(
        () => {
          setFirstName('')
          setLastName('')
          setCompany('')
          setEmail('')
          setPhone('')
          setDescription('')
          setSubmitted(false)
          setSending(false)
          setShowSuccess(true)
        },
        () => {
          setSending(false)
          setError('Unable to send message. Please try again.')
        },
      )
  }

  return (
    <>
      <section
        id="contact"
        className="scroll-mt-20 bg-gradient-to-b from-indigo-50 to-white py-14 sm:py-16"
      >
        <div className="mx-auto max-w-2xl px-6 text-center lg:px-8">
          <h3 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            Hello there! we&apos;ll be glad to{' '}
            <span className="text-indigo-600">hear from you.</span>
          </h3>
          <p className="mt-4 text-md text-gray-600">
            Simplify your payment development and certification with Accezz Minds
          </p>

          <form className="mt-12 space-y-5 text-left" onSubmit={handleSubmit}>
            {/* First name + Last name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-900">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass(fieldInvalid(firstName))}
                  placeholder="First name"
                />
              </div>
              <div>
                <label htmlFor="contact-lastname" className="block text-sm font-semibold text-gray-900">
                  Last name <span className="text-gray-400 font-normal text-xs">(optional)</span>
                </label>
                <input
                  id="contact-lastname"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass(false)}
                  placeholder="Last name"
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <label htmlFor="contact-company" className="block text-sm font-semibold text-gray-900">
                Company <span className="text-red-500">*</span>
              </label>
              <input
                id="contact-company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={inputClass(fieldInvalid(company))}
                placeholder="Your company name"
              />
            </div>

            {/* Email + Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-900">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass(fieldInvalid(email, true))}
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-900">
                  Phone <span className="text-gray-400 font-normal text-xs">(optional)</span>
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClass(false)}
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-900">
                Your message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="contact-message"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                className={inputClass(fieldInvalid(description))}
                placeholder="How can we help you?"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={sending}
                className="w-full rounded-xl bg-indigo-600 py-3.5 text-base font-semibold text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-indigo-400 hover:not-disabled:bg-indigo-500"
              >
                {sending ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Sending…
                  </span>
                ) : 'Submit'}
              </button>
            </div>

            {error && (
              <p className="text-center text-sm text-red-600">{error}</p>
            )}
          </form>
        </div>
      </section>

      {/* Success popup */}
      {showSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setShowSuccess(false)}
        >
          <div
            className="relative mx-4 flex flex-col items-center rounded-2xl bg-white px-10 py-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="/success.gif"
              alt="Success"
              className="h-40 w-40 object-contain"
            />
            <h3 className="mt-4 text-2xl font-bold text-gray-900">Message sent!</h3>
            <p className="mt-2 text-center text-gray-500">
              Thanks for reaching out. We'll get back to you soon.
            </p>
            <button
              onClick={() => setShowSuccess(false)}
              className="mt-6 rounded-xl bg-indigo-600 px-8 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  )
}
